/**
 * API client for the Hono worker (contract: API.md).
 *
 *   import { api, ApiError } from './lib/api.js';
 *   const { user } = await api.get('/auth/me');
 *   await api.post('/deal/moves', { moves: [...] });
 *
 * - Always `credentials: 'include'` (session cookie on .fairplaydeck.com).
 * - Mutations always send a JSON body (`{}` when none) — the API's CSRF rule requires it.
 * - Non-2xx responses throw `ApiError { status, code, message, details, requestId }`.
 * - Mock mode (`?mock=1`, persisted in sessionStorage) routes every call to an in-memory
 *   backend in `./mock/` instead of the network.
 */

export const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || '/api';

export class ApiError extends Error {
  /**
   * @param {number} status
   * @param {string} code
   * @param {string} message
   * @param {any} [details]
   * @param {string} [requestId]
   */
  constructor(status, code, message, details, requestId) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

const MOCK_KEY = 'fp:mock';

/** Mock mode: `?mock=1` (seeded, onboarded household) or `?mock=new` (brand-new user). */
export function mockMode() {
  try {
    const q = new URLSearchParams(window.location.search).get('mock');
    if (q === '0' || q === 'off') {
      sessionStorage.removeItem(MOCK_KEY);
      return null;
    }
    if (q) sessionStorage.setItem(MOCK_KEY, q === '1' ? 'demo' : q);
    return sessionStorage.getItem(MOCK_KEY);
  } catch {
    return null;
  }
}

/** @type {Promise<(method: string, path: string, body?: any) => Promise<any>> | null} */
let mockHandler = null;

/**
 * @param {string} method
 * @param {string} path
 * @param {any} [body]
 */
async function request(method, path, body) {
  const scenario = mockMode();
  if (scenario) {
    mockHandler ??= import('./mock/server.js').then((m) => m.createMockServer(scenario));
    const handle = await mockHandler;
    return handle(method, path, body);
  }

  const init = /** @type {RequestInit} */ ({
    method,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (method !== 'GET') {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body ?? {});
  }

  let res;
  try {
    res = await fetch(`${API_ORIGIN}${path}`, init);
  } catch {
    throw new ApiError(
      0,
      'network_error',
      "We couldn't reach Fair Play Deck. Check your connection.",
    );
  }

  const requestId = res.headers.get('x-request-id') ?? undefined;
  const text = await res.text();
  /** @type {any} */
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    const e = data?.error;
    const code = typeof e === 'string' ? e : (e?.code ?? 'http_error');
    const message = (typeof e === 'object' && e?.message) || defaultMessage(res.status);
    throw new ApiError(res.status, code, message, e?.details, e?.requestId ?? requestId);
  }
  return data;
}

/** @param {number} status */
function defaultMessage(status) {
  if (status === 401) return 'Please sign in again.';
  if (status === 404) return "We couldn't find that.";
  if (status === 429) return 'Too many tries. Give it a minute.';
  if (status >= 500) return 'Something went wrong on our side. Try again in a moment.';
  return 'Something went wrong.';
}

export const api = {
  /** @param {string} path */
  get: (path) => request('GET', path),
  /** @param {string} path @param {any} [body] */
  post: (path, body) => request('POST', path, body),
  /** @param {string} path @param {any} [body] */
  put: (path, body) => request('PUT', path, body),
  /** @param {string} path @param {any} [body] */
  patch: (path, body) => request('PATCH', path, body),
  /** @param {string} path @param {any} [body] */
  del: (path, body) => request('DELETE', path, body),
};

/**
 * Friendly copy for an error, keyed by ApiError code. Falls back to the server's message
 * (always safe to show per API.md §1.3).
 * @param {unknown} err
 */
export function errorMessage(err) {
  if (!(err instanceof ApiError)) return 'Something went wrong. Try again?';
  const byCode = {
    network_error: err.message,
    unauthorized: 'Your session ended. Please sign in again.',
    token_expired: 'That link has expired. Links last 15 minutes — request a fresh one.',
    token_used: 'That link was already used. Request a new one if you need to sign in.',
    token_invalid: "That link doesn't look right. Request a new one.",
    invite_revoked: 'This invite was cancelled. Ask your partner to send a new one.',
    household_full: 'That household already has two players.',
    already_member: "You're already in this household.",
    stale_card: 'Your partner just changed this card. We refreshed it.',
    cannot_agree_own_msc: 'You wrote this standard, so your partner gives the nod.',
    card_locked: 'Personal cards stay with their player — that one can’t move.',
    card_not_held: 'Deal this card to someone before setting its standard.',
    nothing_to_undo: 'Nothing left to undo.',
    checkin_in_progress: 'A check-in is already underway.',
    rate_limited: 'Too many tries. Give it a minute and try again.',
    csrf_failed: 'Your browser blocked that request. Refresh and try again.',
  };
  return byCode[/** @type {keyof typeof byCode} */ (err.code)] ?? err.message;
}
